/**
 * Pure URL-query-string state. No React, no router, no side effects.
 *
 * A schema maps a param name to a spec that knows how to parse it from the
 * query string and serialize it back. `serialize` returns `null` for a value
 * equal to its default, so an unfiltered view produces a clean URL rather than
 * `?group=all&online=all&battery_low=false`.
 */

export interface ParamSpec<T> {
  readonly default: T
  parse(raw: string | null): T
  serialize(value: T): string | null
}

// `any` is required to hold heterogeneous specs in one record; StateOf recovers
// the precise type per key.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type Schema = Record<string, ParamSpec<any>>

export type StateOf<S extends Schema> = {
  [K in keyof S]: S[K] extends ParamSpec<infer T> ? T : never
}

export function parseUrlState<S extends Schema>(schema: S, params: URLSearchParams): StateOf<S> {
  const out: Record<string, unknown> = {}
  for (const key of Object.keys(schema)) {
    const spec = schema[key]
    try {
      out[key] = spec.parse(params.get(key))
    } catch {
      out[key] = spec.default
    }
  }
  return out as StateOf<S>
}

/**
 * Serialize `state` over `base`, preserving any param not named in the schema
 * (e.g. `?event=`, tracking params). Schema params equal to their default are
 * deleted rather than written.
 */
export function serializeUrlState<S extends Schema>(
  schema: S,
  state: StateOf<S>,
  base?: URLSearchParams,
): URLSearchParams {
  const next = new URLSearchParams(base)
  for (const key of Object.keys(schema)) {
    const raw = schema[key].serialize(state[key])
    if (raw === null || raw === '') next.delete(key)
    else next.set(key, raw)
  }
  return next
}

// ---------------------------------------------------------------------------
// Spec builders
// ---------------------------------------------------------------------------

/** Strict positive-integer test — `parseInt` would accept '12abc' and yield 12. */
function parsePositiveInt(raw: string): number | null {
  if (!/^\d+$/.test(raw)) return null
  const n = Number.parseInt(raw, 10)
  return n > 0 ? n : null
}

export function stringParam(def = ''): ParamSpec<string> {
  return {
    default: def,
    parse: (raw) => raw ?? def,
    serialize: (v) => (v === def ? null : v),
  }
}

export function enumParam<T extends string>(values: readonly T[], def: T): ParamSpec<T> {
  return {
    default: def,
    parse: (raw) => (raw !== null && (values as readonly string[]).includes(raw) ? (raw as T) : def),
    serialize: (v) => (v === def ? null : v),
  }
}

export function boolParam(def = false): ParamSpec<boolean> {
  return {
    default: def,
    parse: (raw) => {
      if (raw === 'true') return true
      if (raw === 'false') return false
      return def // absent or malformed
    },
    serialize: (v) => (v === def ? null : String(v)),
  }
}

export function intParam(def: number | null = null): ParamSpec<number | null> {
  return {
    default: def,
    parse: (raw) => {
      if (raw === null) return def
      return parsePositiveInt(raw) ?? def
    },
    serialize: (v) => (v === null || v === def ? null : String(v)),
  }
}

/** CamerasPage group filter: 'all' | 'ungrouped' | <positive group id>. */
export type GroupFilterValue = 'all' | 'ungrouped' | number

export function groupParam(): ParamSpec<GroupFilterValue> {
  return {
    default: 'all',
    parse: (raw) => {
      if (raw === null || raw === 'all') return 'all'
      if (raw === 'ungrouped') return 'ungrouped'
      return parsePositiveInt(raw) ?? 'all'
    },
    serialize: (v) => (v === 'all' ? null : String(v)),
  }
}
